/**
 * @fileOverview 网络请求相关函数实现
 */
import {parseFunction, parseParam, replaceDynamicParameter} from '../index';
import {JSON_HEADER, requestHeaders} from '../../request';
import {DYNAMIC_PARAMETER_TYPE} from '../constant';
import {parseNDJSON} from '../../index';
import {apiDataResultMapping} from "../../../api/function_service";

/**
 * 每帧最多执行多少条 parseFunction（与 requestAnimationFrame 配合，将 DOM 更新限制在每帧一批，
 * 避免主线程长时间占用 + 降低 reflow/repaint 频率）
 */
const SSE_BATCH_PER_FRAME = 5;

/**
 * 调用API
 * @param func
 * @param customParamsList
 * @param customFunctionParams
 * @param instanceId
 */
export const callApi = async (func, customParamsList, customFunctionParams, instanceId) => {
  // 参数个数
  let inParams = _get(func, 'inParams');
  let inParamsSize = _size(inParams);
  if (inParamsSize == 0) {
    return {};
  }
  let api: any = {}, data = {}, headers = {}
  for (let i = 0; i < inParamsSize; i++) {
    let inParam = inParams[i];
    if (i == 0) {
      if (inParam.paramType === 'apiInfo') {
        api = await parseParam(inParam, customParamsList, customFunctionParams, instanceId);
      } else {
        data = await parseParam(inParam, customParamsList, customFunctionParams, instanceId);
      }
    } else {
      if (inParam.paramType === 'apiInfo') {
        api = await parseParam(inParam, customParamsList, customFunctionParams, instanceId);
      } else {
        // 请求头 合并
        let header = await parseParam(inParam, customParamsList, customFunctionParams, instanceId);
        // 允许使用字符串的请求头
        if (typeof (header) == 'string') {
          try {
            header = JSON.parse(header);
          } finally {
          }
        }
        headers = Object.assign(headers, header);
      }
    }
  }
  let defaultHeader: any = Object.assign(requestHeaders(), data instanceof FormData ? {} : JSON_HEADER());
  headers = Object.assign(defaultHeader, headers);
  let {url, method, interfaceId} = api;
  let body = {
    method: method,
    headers: headers,
  };
  if (method.toLowerCase() == 'get') {
    url = url + (url.includes('?') ? '&' : '?') + new URLSearchParams(data).toString();
  } else {
    body['body'] = data instanceof FormData ? data : JSON.stringify(data);
  }
  let apiResponse = await (await fetch(url, body)).json();
  const apiData = apiResponse || {};
  return await apiDataResultMapping({apiData: apiData, interfaceId: interfaceId})
};

/**
 * 事件流（SSE）
 *
 * 卡顿原因简要分析：
 * 1. 主线程长时间占用：循环内对每条 SSE 消息的每条 result 都 await parseFunction()，parseFunction 内部会
 *    setAttrImpl → 更新组件状态 → 触发展示层追加 DOM。切到别的应用再切回时，积压的大量 chunk 会被连续
 *    处理，主线程一直不交还，无法 layout/paint/响应用户输入，导致卡顿。
 * 2. 无“让出”主线程：没有在若干条数据处理后 yield（如 requestAnimationFrame / setTimeout(0)），
 *    会一口气处理完积压数据。
 * 3. 每条数据都做深拷贝：对每条 newData 都 _cloneDeep(customParamsList) 和 _cloneDeep(inParams[...])，
 *    数据量大时 CPU/内存压力大，加重卡顿。
 * 4. DOM 更新过于频繁：每条 result 触发一次完整 parseFunction → 组件更新 → DOM，没有批量或节流，
 *    reflow/repaint 次数过多。
 * 5. 后台标签页堆积：切出浏览器时，后台 tab 的 JS 被节流，reader.read() 消费变慢，SSE 数据在缓冲区堆积；
 *    切回时一次性被读出，循环短时间处理大量事件，导致一次性追加大量 DOM，加剧卡顿。
 *
 * 优化思路：队列 + 按帧消费（读流只解析并入队；消费者每 requestAnimationFrame 最多处理 SSE_BATCH_PER_FRAME 条），
 * 主线程不再被长时间占用，DOM 更新被限制为每帧一批，减轻卡顿；并缓存 functionBody 减少重复 clone。
 *
 * @param func
 * @param customParamsList
 * @param customFunctionParams
 * @param instanceId
 */
/**
 * 事件流（SSE）
 *
 * 相对原实现只改两点：
 * 1. fetch 成功（拿到 response）后立即 return，不再等流结束；
 * 2. 原 fetch 之后的监听逻辑原样包进不 await 的后台协程，并加 try/finally 兜底。
 *
 * 注意：返回时只代表"连接已建立"，数据仍在后台陆续通过 parseFunction 推送。
 */
export const createEventSource = async (func, customParamsList, customFunctionParams, instanceId) => {
  let inParams = _get(func, 'inParams');
  let inParamsSize = _size(inParams);
  if (inParamsSize == 0) {
    return {};
  }
  let api: any = {}, data = {}, headers = {};
  for (let i = 0; i < inParamsSize - 1; i++) {
    let inParam = inParams[i];
    if (i == 0) {
      if (inParam.paramType === 'apiInfo') {
        api = await parseParam(inParam, customParamsList, customFunctionParams, instanceId);
      } else {
        data = await parseParam(inParam, customParamsList, customFunctionParams, instanceId);
      }
    } else {
      if (inParam.paramType === 'apiInfo') {
        api = await parseParam(inParam, customParamsList, customFunctionParams, instanceId);
      } else {
        let header = await parseParam(inParam, customParamsList, customFunctionParams, instanceId);
        if (typeof (header) == 'string') {
          try {
            header = JSON.parse(header);
          } finally {
          }
        }
        headers = Object.assign(headers, header);
      }
    }
  }
  let defaultHeader: any = Object.assign(requestHeaders(), data instanceof FormData ? {} : JSON_HEADER());
  headers = Object.assign(defaultHeader, headers);
  let {url, method} = api;

  /** 回调的 functionBody 只 clone 一次，避免每条 result 都深拷贝 inParams */
  const cachedFunctionBody =
      inParamsSize > 2 ? _get(_cloneDeep(inParams[inParamsSize - 1]), 'functionBody') : null;

  const abortController = new AbortController();
  let body = {
    method: method,
    headers: headers,
    signal: abortController.signal,
  };
  if (method.toLowerCase() == 'get') {
    url = url + (url.includes('?') ? '&' : '?') + new URLSearchParams(data).toString();
  } else {
    body['body'] = data instanceof FormData ? data : JSON.stringify(data);
  }

  // 建立连接（只等响应头），成功后立即返回
  const response = await fetch(url, body);
  if (!response.body) {
    return {};
  }

  // 后台消费：原监听逻辑原样保留，不 await
  (async () => {
    const pendingQueue: any[] = [];
    let streamEnded = false;
    const consumerPromise = (async () => {
      while (!streamEnded || pendingQueue.length > 0) {
        await new Promise<void>((r) => requestAnimationFrame(() => r()));
        const batchSize = Math.min(SSE_BATCH_PER_FRAME, pendingQueue.length);
        for (let i = 0; i < batchSize; i++) {
          const customParamsListCopy = pendingQueue.shift();
          if (cachedFunctionBody && customParamsListCopy != null) {
            await parseFunction(cachedFunctionBody, customParamsListCopy, customFunctionParams, instanceId);
          }
        }
      }
    })();

    let isClosed = false;
    const reader = response.body!.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (!isClosed) {
        const {value, done} = await reader.read();
        if (done) {
          if (buffer.trim()) {
            const parsedEvent = parseEvent(buffer);
            if (parsedEvent.type == 'message') {
              const resultList = parseNDJSON(parsedEvent.data);
              for (const newData of resultList) {
                const customParamsListCopy = _cloneDeep(customParamsList);
                replaceDynamicParameter(customParamsListCopy, DYNAMIC_PARAMETER_TYPE.EVENT, newData);
                if (cachedFunctionBody) pendingQueue.push(customParamsListCopy);
              }
            }
          }
          break;
        }
        if (isClosed) {
          break;
        }
        buffer += decoder.decode(value, {stream: true});
        let eventEndIndex;
        while ((eventEndIndex = buffer.indexOf('\n\n')) !== -1) {
          const eventText = buffer.substring(0, eventEndIndex + 2);
          buffer = buffer.substring(eventEndIndex + 2);
          const parsedEvent = parseEvent(eventText);
          if (parsedEvent.type == 'message') {
            const resultList = parseNDJSON(parsedEvent.data);
            for (const newData of resultList) {
              const customParamsListCopy = _cloneDeep(customParamsList);
              replaceDynamicParameter(customParamsListCopy, DYNAMIC_PARAMETER_TYPE.EVENT, newData);
              if (cachedFunctionBody) pendingQueue.push(customParamsListCopy);
            }
          } else if (parsedEvent.type == 'close') {
            isClosed = true;
            abortController.abort();
            break;
          }
        }
      }
    } catch (e) {
      console.error('[SSE] stream error:', e);
    } finally {
      // 必须置 true，否则 consumer 永远等 rAF
      streamEnded = true;
      await consumerPromise;
    }
  })();

  return {};
};

export const closeEventSource = async (func, customParamsList, customFunctionParams, instanceId) => {
  let eventSource = await parseParam(_get(func, 'inParams')[0], customParamsList, customFunctionParams, instanceId);
  eventSource.close();
};

/**
 * 解析单个事件
 * @param {string} event - 事件字符串
 * @returns {Object} 解析后的事件对象
 */
const parseEvent = (event) => {
  const lines = event.split('\n');
  let eventType = 'message';
  let data = '';
  let id = null;
  let retry = null;
  let isComment = false;

  for (const line of lines) {
    // 跳过空行
    if (!line.trim()) continue;

    // 处理注释行
    if (line.startsWith(':')) {
      isComment = true;
      continue;
    }

    // 查找冒号位置
    const colonIndex = line.indexOf(':');
    if (colonIndex === -1) continue;

    const field = line.substring(0, colonIndex);
    let value = line.substring(colonIndex + 1);

    // 如果值以空格开头，移除空格
    if (value.startsWith(' ')) {
      value = value.substring(1);
    }

    switch (field) {
      case 'event':
        eventType = value;
        break;
      case 'data':
        // 多行数据的处理
        data += (data ? '\n' : '') + value;
        break;
      case 'id':
        id = value;
        break;
      case 'retry':
        // 尝试将重试值转换为数字
        const parsedRetry = parseInt(value, 10);
        if (!isNaN(parsedRetry)) {
          retry = parsedRetry;
        }
        break;
    }
  }

  const parsedEvent = {
    type: isComment ? 'comment' : eventType,
    data,
    id,
    retry,
  };

  // // 输出解析后的事件对象，避免控制台截断
  // console.log('parseEvent------> 原始字符串长度:', event.length);
  // console.log('parseEvent------> 解析后的事件对象:', parsedEvent);
  //
  // // 如果 data 是 JSON 字符串，尝试解析并输出
  // if (parsedEvent.data) {
  //   console.log('parseEvent------> data 字符串长度:', parsedEvent.data.length);
  //   try {
  //     const parsedData = JSON.parse(parsedEvent.data);
  //     console.log('parseEvent------> 解析后的 data 内容:', parsedData);
  //   } catch (e) {
  //     // 如果不是有效的 JSON，输出错误信息和原始 data 字符串的前后部分
  //     console.error('parseEvent------> JSON 解析失败:', e.message);
  //     console.log('parseEvent------> data 前100字符:', parsedEvent.data.substring(0, 100));
  //     console.log('parseEvent------> data 后100字符:', parsedEvent.data.substring(Math.max(0, parsedEvent.data.length - 100)));
  //   }
  // }

  return parsedEvent;
};
